import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { get, post, put, del, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

// Personalization Templates — CRUD over the Firestore `personalizationTemplates`
// catalog (shared with productconfig-dashboard) plus the storefront-wide V2
// switch (Shopify shop metafield ghostfit.personalization_v2_enabled). The
// server validates every save; this page surfaces its `details[]` verbatim.

type Field = {
  id: string;
  label?: string;
  type: 'text' | 'numeric';
  maxChars: number;
  uppercase?: boolean;
  placeholder?: string;
};

type Template = {
  id: string;
  displayName: string;
  description?: string;
  fields: Field[];
  physicalLocation: string;
  defaultDecorationMethod: string;
  availableFonts: string[];
  availableInkColors: string[];
  defaultInkColor?: string;
  pricing: { addon: number };
  defaultPhysicalSize?: { w: number; h: number };
  inputUx?: string | null;
  defaultFieldLayout?: Record<string, unknown>;
};

// Codes match lib/personalization-templates VALID_LOCATIONS / VALID_DECORATION.
const LOCATIONS: [string, string][] = [
  ['FF', 'FF — Full Front'], ['FC', 'FC — Front Center'], ['FB', 'FB — Full Back'],
  ['LC', 'LC — Left Chest'], ['LL', 'LL — Left Leg'], ['CH', 'CH — Center Hat'],
  ['RH', 'RH — Right Hat'], ['LH', 'LH — Left Hat'], ['LS', 'LS — Left Sleeve'], ['RS', 'RS — Right Sleeve'],
];
const DECORATIONS = ['vinyl', 'embroidery', 'screen-print', 'dtf'];

const EMPTY: Template = {
  id: '', displayName: '', description: '',
  fields: [{ id: 'name', label: 'Name', type: 'text', maxChars: 12, uppercase: true, placeholder: 'SMITH' }],
  physicalLocation: 'FB', defaultDecorationMethod: 'vinyl',
  defaultPhysicalSize: { w: 10, h: 4 },
  availableFonts: ['athletic-block', 'modern'],
  availableInkColors: ['white', 'black', 'school-primary', 'school-secondary'],
  defaultInkColor: 'school-primary',
  pricing: { addon: 6 },
};

// Collision-safe id for "+ field" (nextZoneId idiom in PersonalizationAssign):
// `field${length+1}` alone duplicates ids after delete-then-add — find the
// smallest N where field${N} isn't already taken.
function nextFieldId(fields: Field[]): string {
  const taken = new Set(fields.map((f) => f.id));
  let n = 1;
  while (taken.has(`field${n}`)) n += 1;
  return `field${n}`;
}

// 400s carry validator `details[]`; 500s are `{error:'server_error', message}`.
function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string; details?: string[] } | null;
    if (body?.details?.length) return body.details.join(' · ');
    return body?.message || body?.error || `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : String(e);
}

export default function PersonalizationTemplates() {
  const { data, isLoading, mutate } = useSWR<{ templates: Template[] }>('/api/personalization/templates', get);
  // Switch read fails when Shopify creds aren't wired on the runtime — show
  // "unavailable" instead of retry-looping a known-broken call.
  const sw = useSWR<{ enabled: boolean }>('/api/personalization/global-switch', get, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const [editing, setEditing] = useState<Template | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const templates = [...(data?.templates || [])].sort((a, b) =>
    String(a.displayName).localeCompare(String(b.displayName)));

  async function toggleSwitch() {
    if (!sw.data) return;
    const next = !sw.data.enabled;
    if (!confirm(`Turn Personalization V2 ${next ? 'ON' : 'OFF'} storefront-wide?`)) return;
    try {
      await post('/api/personalization/global-switch', { enabled: next });
      await sw.mutate({ enabled: next }, { revalidate: false });
      toast.success(`Personalization V2 is now ${next ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error(`Switch failed: ${errMsg(e)}`);
    }
  }

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      if (isNew) await post('/api/personalization/templates', editing);
      else await put(`/api/personalization/templates/${encodeURIComponent(editing.id)}`, editing);
      toast.success(`Saved ${editing.displayName || editing.id}`);
      setEditing(null);
      void mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(t: Template) {
    if (!confirm(`Delete template "${t.displayName}" (${t.id})? Products still referencing it will fail to sync.`)) return;
    try {
      await del(`/api/personalization/templates/${encodeURIComponent(t.id)}`);
      toast.success(`Deleted ${t.displayName}`);
      void mutate();
    } catch (e) {
      toast.error(`Delete failed: ${errMsg(e)}`);
    }
  }

  const setField = (i: number, patch: Partial<Field>) => {
    if (!editing) return;
    setEditing({ ...editing, fields: editing.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  };

  const csv = (xs: string[] | undefined) => (xs || []).join(', ');
  const parseCsv = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);

  return (
    <div className="px-7 py-6">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Personalization Templates</h1>
          <p className="text-[12px] text-muted">{templates.length} templates · new / unset shops default V2 ON</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" disabled={!sw.data} onClick={() => void toggleSwitch()}>
            Personalization V2:{' '}
            <span className={sw.data?.enabled ? 'font-semibold text-success' : 'font-semibold'}>
              {sw.error ? 'unavailable' : sw.data ? (sw.data.enabled ? 'ON' : 'OFF') : '…'}
            </span>
          </Button>
          <Button onClick={() => { setEditing(structuredClone(EMPTY)); setIsNew(true); }}>New template</Button>
        </div>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3"><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-full" /></div>
        ) : !templates.length ? (
          <div className="p-8 text-center text-[12px] text-muted">No personalization templates yet.</div>
        ) : (
          templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 border-b border-divider px-3 py-2 last:border-b-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium">
                  {t.displayName}
                  <span className="ml-2 font-mono text-[10px] text-subtle">{t.id}</span>
                </div>
                {t.description && <div className="truncate text-[11px] text-muted">{t.description}</div>}
              </div>
              <Badge className="bg-bg text-muted">{t.physicalLocation}</Badge>
              <Badge className="bg-bg text-muted">{t.defaultDecorationMethod}</Badge>
              <span className="w-14 shrink-0 text-right text-[11px] text-muted">
                {(t.fields || []).length} field{(t.fields || []).length === 1 ? '' : 's'}
              </span>
              <span className="w-12 shrink-0 text-right text-[12px] font-medium">${t.pricing?.addon ?? 0}</span>
              <button className="text-[11px] text-muted hover:text-text" onClick={() => { setEditing(structuredClone(t)); setIsNew(false); }}>Edit</button>
              <button className="text-[11px] text-red-600 hover:text-red-700" onClick={() => void remove(t)}>Delete</button>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!busy && !o) setEditing(null); }}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isNew ? 'New template' : `Edit ${editing?.displayName || editing?.id || 'template'}`}</DialogTitle>
            <DialogDescription>
              Writes the <code>personalizationTemplates</code> catalog. The server validates on save.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4 text-[12px]">
              <div className="grid grid-cols-2 gap-3">
                <Text label="ID (kebab-case)" value={editing.id} disabled={!isNew} placeholder="sleeve-name-number"
                  onChange={(v) => setEditing({ ...editing, id: v })} />
                <Text label="Display name" value={editing.displayName} placeholder="Sleeve — Name or Number"
                  onChange={(v) => setEditing({ ...editing, displayName: v })} />
              </div>
              <Text label="Description" value={editing.description || ''}
                onChange={(v) => setEditing({ ...editing, description: v })} />

              <div className="grid grid-cols-3 gap-3">
                <Select label="Location" value={editing.physicalLocation} options={LOCATIONS}
                  onChange={(v) => setEditing({ ...editing, physicalLocation: v })} />
                <Select label="Decoration" value={editing.defaultDecorationMethod}
                  options={DECORATIONS.map((d) => [d, d] as [string, string])}
                  onChange={(v) => setEditing({ ...editing, defaultDecorationMethod: v })} />
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">Addon $</span>
                  <input type="number" min={0} step={0.5}
                    value={Number.isFinite(editing.pricing?.addon) ? editing.pricing.addon : ''}
                    onChange={(e) => setEditing({ ...editing, pricing: { ...editing.pricing, addon: Number(e.target.value) } })}
                    className={inputCls} />
                </label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-subtle">Fields (min 1)</span>
                  <button type="button" className="rounded-sm border border-border bg-surface px-2 py-1 text-[11px] hover:bg-bg"
                    onClick={() => setEditing({
                      ...editing,
                      fields: [...editing.fields, { id: nextFieldId(editing.fields), label: '', type: 'text', maxChars: 12 }],
                    })}>
                    + field
                  </button>
                </div>
                {editing.fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_92px_64px_1fr_28px] items-end gap-2 rounded-sm border border-border p-2">
                    <Text small label="id" value={f.id} onChange={(v) => setField(i, { id: v })} />
                    <Text small label="label" value={f.label || ''} onChange={(v) => setField(i, { label: v })} />
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">type</span>
                      <select value={f.type} onChange={(e) => setField(i, { type: e.target.value as Field['type'] })} className={inputCls}>
                        <option value="text">text</option>
                        <option value="numeric">numeric</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">maxChars</span>
                      <input type="number" min={1} value={Number.isFinite(f.maxChars) ? f.maxChars : ''}
                        onChange={(e) => setField(i, { maxChars: Number(e.target.value) })} className={inputCls} />
                    </label>
                    <Text small label="placeholder" value={f.placeholder || ''} onChange={(v) => setField(i, { placeholder: v })} />
                    <button type="button" disabled={editing.fields.length === 1}
                      title={editing.fields.length === 1 ? 'Templates need at least one field' : 'Remove field'}
                      className="h-[30px] w-7 rounded-sm border border-border text-[12px] text-muted hover:bg-bg disabled:opacity-40"
                      onClick={() => setEditing({ ...editing, fields: editing.fields.filter((_, j) => j !== i) })}>
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Text label="Fonts (comma-separated)" value={csv(editing.availableFonts)}
                  onChange={(v) => setEditing({ ...editing, availableFonts: parseCsv(v) })} />
                <Text label="Ink colors (comma-separated)" value={csv(editing.availableInkColors)}
                  onChange={(v) => setEditing({ ...editing, availableInkColors: parseCsv(v) })} />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- local field helpers (PrizeProductDialog idiom) ---
const inputCls = 'mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px] disabled:opacity-60';

function Text({ label, value, onChange, placeholder, disabled, small }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean; small?: boolean;
}) {
  return (
    <label className="block">
      <span className={`block ${small ? 'text-[10px]' : 'text-[11px]'} uppercase tracking-[0.04em] text-muted`}>{label}</span>
      <input value={value} disabled={disabled} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </label>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
